import {Transition} from '@headlessui/react';
import {useCallback, useEffect, useState} from 'react';

import {NotificationT} from '@/lib/types/notifications';

import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent} from '@/components';
import Icon from '@/components/icon';

type NotificationItemProps = {
  notification: NotificationT;
};

export function NotificationItem({notification}: NotificationItemProps) {
  const {markAsRead, archiveNotification, unarchiveNotification, removeNotification} = useNotifications();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState<'read' | 'archive' | 'unarchive' | 'delete' | null>(null);
  const [show, setShow] = useState(true);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');

  const handleClick = useCallback(async () => {
    if (notification.status === 'unread') {
      try {
        setIsLoading('read');
        await markAsRead(notification.notification_id);
      } finally {
        setIsLoading(null);
      }
    }
  }, [markAsRead, notification.notification_id, notification.status]);

  const handleMarkAsRead = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent the click from triggering the parent div's onClick
      try {
        setIsLoading('read');
        await markAsRead(notification.notification_id);
      } finally {
        setIsLoading(null);
      }
    },
    [markAsRead, notification.notification_id]
  );

  const handleArchive = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent the click from triggering the parent div's onClick
      try {
        setIsLoading('archive');
        setSlideDirection('right');
        setShow(false);
        // Wait for animation to complete before archiving
        setTimeout(async () => {
          await archiveNotification(notification.notification_id);
        }, 300);
      } finally {
        setIsLoading(null);
      }
    },
    [archiveNotification, notification.notification_id]
  );

  const handleUnarchive = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent the click from triggering the parent div's onClick
      try {
        setIsLoading('unarchive');
        setSlideDirection('left');
        setShow(false);
        // Wait for animation to complete before unarchiving
        setTimeout(async () => {
          await unarchiveNotification(notification.notification_id);
        }, 300);
      } finally {
        setIsLoading(null);
      }
    },
    [unarchiveNotification, notification.notification_id]
  );

  const handleDelete = useCallback(async () => {
    try {
      setIsLoading('delete');
      setSlideDirection('right');
      setShow(false);
      setIsDeleteModalVisible(false);
      // Wait for animation to complete before deleting
      setTimeout(async () => {
        await removeNotification(notification.notification_id);
      }, 300);
    } finally {
      setIsLoading(null);
    }
  }, [removeNotification, notification.notification_id]);

  useEffect(() => {
    setShow(true);
  }, [notification.notification_id]);

  const isArchived = notification.status === 'archived';
  const isUnread = notification.status === 'unread';

  return (
    <div className="overflow-hidden">
      <Transition
        show={show}
        appear
        enter="transform transition-all duration-300 ease-out"
        enterFrom="opacity-0 translate-x-4"
        enterTo="opacity-100 translate-x-0"
        leave="transform transition-all duration-300 ease-in"
        leaveFrom="opacity-100 translate-x-0"
        leaveTo={`opacity-0 ${slideDirection === 'right' ? 'translate-x-full' : '-translate-x-full'}`}
      >
        <div
          onClick={handleClick}
          className={`group flex cursor-pointer items-start justify-between rounded-lg border p-4 shadow-sm transition-all duration-200 hover:shadow-md ${
            isUnread
              ? 'border-blue-200 bg-blue-50 hover:bg-blue-100/70 dark:border-blue-800 dark:bg-blue-900/20 dark:hover:bg-blue-900/30'
              : isArchived
                ? 'border-gray-300 bg-gray-50 hover:bg-gray-100/70 dark:border-gray-600 dark:bg-gray-700/50 dark:hover:bg-gray-700/60'
                : 'border-gray-200 bg-white hover:bg-gray-50/70 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-800/90'
          }`}
        >
          <div className="flex-1">
            <h3
              className={`text-base transition-colors duration-200 ${
                isUnread
                  ? 'text-blue-900 dark:text-blue-100 font-semibold'
                  : isArchived
                    ? 'font-medium text-gray-600 dark:text-gray-300'
                    : 'font-medium text-gray-900 dark:text-white'
              }`}
            >
              {notification.title}
            </h3>
            <p
              className={`mt-1 text-sm transition-colors duration-200 ${
                isUnread
                  ? 'text-blue-800 dark:text-blue-200'
                  : isArchived
                    ? 'text-gray-500 dark:text-gray-400'
                    : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {notification.message}
            </p>
            <div className="mt-2 flex items-center space-x-4 text-sm">
              <span className="text-gray-400">
                {new Date(notification.created_at).toLocaleString(undefined, {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              {notification.link && (
                <a
                  href={notification.link}
                  target={notification.open_in_new_tab ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  onClick={e => e.stopPropagation()} // Prevent the click from triggering the parent div's onClick
                >
                  {notification.link_text || 'View'}
                </a>
              )}
            </div>
          </div>
          <div className="ml-4 flex space-x-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            {isUnread && (
              <button
                onClick={handleMarkAsRead}
                className="bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/40 rounded-md p-2 transition-all duration-200 hover:shadow-sm"
                title="Mark as read"
                disabled={isLoading !== null}
              >
                {isLoading === 'read' ? (
                  <Icon name="refresh" className="h-5 w-5 animate-spin" />
                ) : (
                  <Icon name="check" className="h-5 w-5" />
                )}
              </button>
            )}
            {isArchived ? (
              <>
                <button
                  onClick={handleUnarchive}
                  className="bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-500/30 dark:hover:bg-green-500/40 rounded-md p-2 transition-all duration-200 hover:shadow-sm dark:text-white"
                  title="Restore from archive"
                  disabled={isLoading !== null}
                >
                  {isLoading === 'unarchive' ? (
                    <Icon name="refresh" size="2xl" className="animate-spin" color="warning" />
                  ) : (
                    <Icon name="archive-out" size="2xl" color="warning" />
                  )}
                </button>
                <button
                  onClick={() => setIsDeleteModalVisible(true)}
                  className="bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-500/30 dark:hover:bg-red-500/40 rounded-md p-2 transition-all duration-200 hover:shadow-sm dark:text-white"
                  title="Delete permanently"
                  disabled={isLoading !== null}
                >
                  {isLoading === 'delete' ? (
                    <Icon name="refresh" size="2xl" className="animate-spin" color="danger" />
                  ) : (
                    <Icon name="trash" size="2xl" color="danger" />
                  )}
                </button>
              </>
            ) : (
              <button
                onClick={handleArchive}
                className="bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-500/30 dark:hover:bg-green-500/40 rounded-md p-2 transition-all duration-200 hover:shadow-sm dark:text-white"
                title="Archive"
                disabled={isLoading !== null}
              >
                {isLoading === 'archive' ? (
                  <Icon name="refresh" size="2xl" className="animate-spin" color="success" />
                ) : (
                  <Icon name="archive" size="2xl" color="success" />
                )}
              </button>
            )}
          </div>
        </div>
      </Transition>

      <ConfirmDeleteModalComponent
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        description="Are you sure you want to permanently delete this notification? This action cannot be undone."
        isSubmitting={isLoading === 'delete'}
      />
    </div>
  );
}
